<template>
  <ModalBlock
    v-if="modalSpecialization"
    :model="modalSpecialization"
    @update:modelValue="setSpecializationModalValue"
  >
    <div :class="style.modal">
      <h3>Добавить специализацию</h3>

      <section :class="style.infoBlock">
        <div
          v-for="(specialization, num) in getClinic.specializationList"
          :key="getSpecializationOptionKey(specialization, num)"
          :class="style.specializationCard"
        >
          <span v-text="specialization" />

          <SwitchComponent
            :modelValue="isSpecializationSelected(specialization)"
            @update:modelValue="setSpecializationValue(specialization, $event)"
          />
        </div>
      </section>

      <PrimaryButton
        text="Сохранить изменения"
        :disabled="!statusSaveSpecializationModal"
        @click="applySpecializationModal"
      />
    </div>
  </ModalBlock>

  <ModalBlock
    v-if="modalDirection"
    :model="modalDirection"
    @update:modelValue="setDirectionModalValue"
  >
    <div :class="style.modal">
      <h3>Добавить направление</h3>

      <section :class="style.infoBlock">
        <div
          v-for="(direction, num) in Object.keys(servicesList)"
          :key="getDirectionOptionKey(direction, num)"
          :class="style.specializationCard"
        >
          <span v-text="direction" />

          <SwitchComponent
            :modelValue="isDirectionSelected(direction)"
            @update:modelValue="setDirectionValue(direction, $event)"
          />
        </div>
      </section>

      <PrimaryButton
        text="Сохранить изменения"
        :disabled="!statusSaveDirectionModal"
        @click="applyDirectionModal"
      />
    </div>
  </ModalBlock>

  <ActionModal
    v-model="deleteDoctorModal"
    title="Удалить врача?"
    :actions="buttonDeleteDoctorModal"
  />

  <ActionModal
    v-model="unsavedChangesModal"
    title="Сохранить изменения?"
    subtitle="Вы внесли изменения в панели. Если её закрыть, они не сохранятся."
    :actions="saveModalButtons"
  />

  <PanelComponent
    v-if="selectedDoctorId && localDoctor && localDoctor._id"
    :panelClass="style.doctorPanel"
    @close="closeDoctorPanel"
  >
    <template #header>
      <h3 :class="style.title">Врач</h3>
    </template>

    <AccordionBase
      text="Данные"
      :class="style.accordion"
      :classHeader="[style.accordionHeader]"
      showChevronWithActions
    >
      <template #actions><div /></template>
      <div :class="style.sectionInner">
        <div>
          <input
            ref="photoInput"
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            hidden
            @change="setImgDoctor"
          />

          <InputFieldMedia
            text="Файл"
            description="Аватар"
            :image="getImgDoctor(localDoctor)"
            :actions="actionsUploadDoctorPhoto"
            @upload="openUploadDialog"
          />
        </div>

        <div :class="style.fieldGroup">
          <InputStyle
            description="ФИО"
            placeholder="Введите ФИО"
            size="M"
            v-model="localDoctor.name"
          />
        </div>

        <div :class="style.fieldGroup">
          <InputStyle
            description="Стаж"
            placeholder="Введите стаж"
            size="M"
            v-model="localDoctor.experience"
          />
        </div>

        <div :class="style.fieldGroup">
          <InputStyle
            description="Описание"
            placeholder="Введите описание"
            :textarea="true"
            :minHeightTextArea="172"
            size="M"
            :modelValue="localDoctor.discription ?? ''"
            @update:modelValue="localDoctor.discription = $event"
          />
        </div>
      </div>
    </AccordionBase>

    <AccordionBase
      text="Принимает"
      :class="style.accordion"
      :defaultOpen="false"
      :classHeader="[style.accordionHeader]"
      showChevronWithActions
    >
      <template #actions><div /></template>
      <div :class="style.sectionInner">
        <div :class="style.fieldGroup">
          <div v-if="localDoctor.address" :class="style.checkboxList">
            <div
              :class="style.checkbox"
              v-for="(_, address) in localDoctor.address"
              :key="getAddressCheckboxId(String(address))"
            >
              <label
                :for="getAddressCheckboxId(String(address))"
                :class="style.textCheckbox"
              >
                {{ address }}
              </label>

              <CheckboxComponent
                :id="getAddressCheckboxId(String(address))"
                :key="String(address)"
                name="address"
                :value="String(address)"
                :modelValue="isAddressSelected(String(address))"
                :class="style.checkboxComponent"
                @update:modelValue="setAddressValue(String(address), $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </AccordionBase>

    <AccordionBase
      text="Специализации"
      :class="style.accordion"
      :defaultOpen="false"
      :classHeader="[style.accordionHeader]"
      showChevronWithActions
    >
      <template #actions>
        <ButtonIcon
          :icon="addIcon"
          variant="ghost"
          size="28"
          @click="openSpecializationModal"
        />
      </template>
      <div :class="style.sectionInner">
        <div :class="style.listBlock">
          <div
            v-if="localDoctor.specialization?.length"
            :class="style.listHeaderActions"
          >
            <div
              v-for="(specialization, index) in localDoctor.specialization"
              :key="`${localDoctor._id}-specialization-${specialization}`"
              :class="style.itemList"
            >
              <span :class="style.itemText">{{ specialization }}</span>

              <div
                :class="[
                  style.actions,
                  openedSpecializationIndex === index
                    ? style.showConstantly
                    : null
                ]"
              >
                <ActionMenu
                  :modelValue="openedSpecializationIndex === index"
                  :actions="getSpecializationActions(index)"
                  placement="bottom-end"
                  :offset="8"
                  :width="200"
                  @update:modelValue="setSpecializationMenuValue(index, $event)"
                >
                  <template #trigger="{isOpen, toggle}">
                    <TabIcon
                      :active="isOpen"
                      :icon="moreIcon"
                      size="28"
                      variant="ghost"
                      @click.stop="toggle"
                    />
                  </template>
                </ActionMenu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccordionBase>

    <AccordionBase
      text="Направления"
      :class="style.accordion"
      :defaultOpen="false"
      :classHeader="[style.accordionHeader]"
      showChevronWithActions
    >
      <template #actions>
        <ButtonIcon
          :icon="addIcon"
          variant="ghost"
          size="28"
          @click="openDirectionModal"
        />
      </template>
      <div :class="style.sectionInner">
        <div :class="style.listBlock">
          <div v-if="modelDirection?.length" :class="style.listHeaderActions">
            <div
              v-for="(direction, index) in modelDirection"
              :key="`${localDoctor._id}-direction-${direction}`"
              :class="style.itemList"
            >
              <span :class="style.itemText">{{ direction }}</span>

              <div
                :class="[
                  style.actions,
                  openedDirectionIndex === index ? style.showConstantly : null
                ]"
              >
                <ActionMenu
                  :modelValue="openedDirectionIndex === index"
                  :actions="getDirectionActions(index)"
                  placement="bottom-end"
                  :offset="8"
                  :width="200"
                  @update:modelValue="setDirectionMenuValue(index, $event)"
                >
                  <template #trigger="{isOpen, toggle}">
                    <TabIcon
                      :active="isOpen"
                      :icon="moreIcon"
                      size="28"
                      variant="ghost"
                      @click.stop="toggle"
                    />
                  </template>
                </ActionMenu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccordionBase>

    <section :class="[style.section, style.fieldGroup]">
      <span :class="style.fieldLabel">Действия</span>

      <SecondaryButton
        text="Удалить врача"
        :icon="deleteIcon"
        @click="openDeleteDoctorModal"
      />
    </section>

    <div :class="style.buttonSaveBlock">
      <PrimaryButton
        text="Сохранить изменения"
        :disabled="!statusSave || isSavingDoctor"
        :icon="
          !statusSave || isSavingDoctor
            ? buttonCheckIconAccent
            : buttonCheckIconDefault
        "
        @click="saveDoctorChanges"
      />
    </div>
  </PanelComponent>

  <LoaderComponent v-if="statusDataLouding === 'loading'" />

  <div v-else :class="style.doctorList">
    <div v-if="statusDataLouding === 'not key'" :class="style.absenceDoctor">
      <h3>Врачи не добавлены, настройте интеграцию с МИС</h3>
      <p>Перейдите в Настройки → Интеграции → МИС</p>
      <button @click="$router.push('/user/integration')">Добавить</button>
    </div>

    <div v-else :class="style.content">
      <SectionHeaderBlock title="Ваши врачи" :class="style.sectionBlock" />

      <div :class="style.doctorListBlock">
        <DoctorsList
          :doctors="visibleData"
          :bufferStatus="bufferStatus"
          :activeDoctorId="localDoctor?._id"
          @openDoctor="openDoctorPanel"
          @toggleStatus="handleDoctorStatusChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {copyObj} from "@/scripts/general";
import {defineComponent, nextTick, markRaw} from "vue";
import DoctorsList from "./components/DoctorsList.vue";
import style from "./DoctorsStyle.module.scss";
import {getObjService} from "@/page/onlineAppointment/OnlineAppointmentScript";
import {DOMAIN} from "@/http";
import {API} from "@/api/generated";
import {clinic, setting} from "@/page/onlineAppointment/clinic/ClinicTypes";
import {doctor} from "@/page/onlineAppointment/doctors/DoctorsType";

import PanelComponent from "@/elements/UI/overlay/panel/PanelComponent.vue";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import SwitchComponent from "@/elements/UI/input/Switch/SwitchComponent.vue";
import CheckboxComponent from "@/elements/UI/input/Checkbox/CheckboxComponent.vue";
import ModalBlock from "@/layout/nav/components/modal/ModalBlock.vue";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";

import imageIcon from "$images/icons/button/Name=Image, Size=16, Tone=Default.svg";
import uploadIcon from "$images/icons/button/Name=Upload, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import deleteIconError from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import moreIcon from "$images/icons/tabs/Name=More, Size=16.svg";
import chevronUpIcon from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDownIcon from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import buttonCheckIconAccent from "$images/icons/button/Name=Check, Size=16, Tone=Accent.svg";
import buttonCheckIconDefault from "$images/icons/input/Name=Check, Size=16, Tone=Default.svg";
import {LegacyDoctorPhoto} from "@/page/quiz/typeQuiz";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";
import ActionModal from "@/layout/nav/components/modal/modals/ActionModal/ActionModal.vue";
import SectionHeaderBlock from "@/elements/UI/section/SectionHeaderBlock/SectionHeaderBlock.vue";
import TabIcon from "@/elements/UI/tabs/TabIcon/TabIcon.vue";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";

export default defineComponent({
  name: "DoctorsComponent",

  components: {
    ButtonIcon,
    LoaderComponent,
    TabIcon,
    SectionHeaderBlock,
    ActionModal,
    InputFieldMedia,
    ActionMenu,
    ModalBlock,
    CheckboxComponent,
    PrimaryButton,
    SwitchComponent,
    InputStyle,
    SecondaryButton,
    AccordionBase,
    PanelComponent,
    DoctorsList
  },

  data() {
    return {
      style,
      DOMAIN,
      getObjService,
      modalSpecialization: false,
      modalDirection: false,
      deleteDoctorModal: false,
      selectedDoctorId: "" as string,
      localDoctor: null as doctor | null,
      savedDoctorSnapshot: "" as string,
      specializationModalSnapshot: "" as string,
      directionModalSnapshot: "" as string,
      serviceModalSnapshot: "" as string,
      openedSpecializationIndex: null as number | null,
      openedDirectionIndex: null as number | null,
      avatarVersion: 0,
      isSavingDoctor: false,
      unsavedChangesModal: false,
      imageIcon,
      uploadIcon,
      deleteIcon,
      deleteIconError,
      addIcon,
      moreIcon,
      chevronDownIcon,
      chevronUpIcon,
      buttonCheckIconAccent,
      buttonCheckIconDefault
    };
  },

  async mounted() {
    window.addEventListener("keydown", this.handleSaveShortcut);

    try {
      await this.$clinicStore.fetchSetting();
      this.setServicesList(this.getSetting);
    } catch (error) {
      console.error("Ошибка при загрузке настроек врачей:", error);
    }
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleSaveShortcut);
  },

  methods: {
    handleSaveShortcut(event: KeyboardEvent): void {
      if (!((event.metaKey || event.ctrlKey) && event.code === "KeyS")) {
        return;
      }

      event.preventDefault();

      if (this.unsavedChangesModal) {
        void this.saveDoctorAndClosePanel();
        return;
      }

      if (this.modalSpecialization) {
        if (this.statusSaveSpecializationModal) {
          this.applySpecializationModal();
        }

        return;
      }

      if (this.modalDirection) {
        if (this.statusSaveDirectionModal) {
          this.applyDirectionModal();
        }

        return;
      }

      if (this.statusSave && !this.isSavingDoctor) {
        void this.saveDoctorChanges();
      }
    },

    getSpecializationOptionKey(specialization: string, index: number): string {
      return `specialization-${specialization || index}`;
    },

    getDirectionOptionKey(direction: string, index: number): string {
      return `direction-${direction || index}`;
    },

    getAddressCheckboxId(address: string): string {
      return `doctor-address-${address
        .toLowerCase()
        .replace(/[^a-zа-яё0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")}`;
    },
    normalizeDoctorPhoto(photo: LegacyDoctorPhoto): string {
      if (typeof photo === "string") {
        return photo;
      }

      if (photo && typeof photo === "object" && !Array.isArray(photo)) {
        if (typeof photo.circle === "string" && photo.circle) {
          return photo.circle;
        }

        if (typeof photo.square === "string" && photo.square) {
          return photo.square;
        }

        const firstStringPhoto = Object.values(photo).find((value) => {
          return typeof value === "string" && value;
        });

        return typeof firstStringPhoto === "string" ? firstStringPhoto : "";
      }

      return "";
    },

    openDeleteDoctorModal(): void {
      this.deleteDoctorModal = true;
    },

    closeDeleteDoctorModal(): void {
      this.deleteDoctorModal = false;
    },

    isAddressSelected(address: string): boolean {
      if (!this.localDoctor?.address) {
        return false;
      }

      return !!this.localDoctor.address[address];
    },

    setAddressValue(address: string, value: boolean): void {
      if (!this.localDoctor) {
        return;
      }

      if (!this.localDoctor.address) {
        this.localDoctor.address = {};
      }

      this.localDoctor.address[address] = value;
    },

    getSpecializationSnapshot(): string {
      if (!this.localDoctor?.specialization) {
        return JSON.stringify([]);
      }

      return JSON.stringify([...this.localDoctor.specialization]);
    },

    openSpecializationModal(): void {
      this.specializationModalSnapshot = this.getSpecializationSnapshot();
      this.modalSpecialization = true;
    },

    setSpecializationModalValue(value: boolean): void {
      if (value) {
        this.openSpecializationModal();
        return;
      }

      this.closeSpecializationModal();
    },

    closeSpecializationModal(): void {
      if (!this.localDoctor) {
        this.modalSpecialization = false;
        return;
      }

      try {
        this.localDoctor.specialization = JSON.parse(
          this.specializationModalSnapshot || "[]"
        );
      } catch {
        this.localDoctor.specialization = [];
      }

      this.modalSpecialization = false;
    },

    applySpecializationModal(): void {
      this.specializationModalSnapshot = this.getSpecializationSnapshot();
      this.modalSpecialization = false;
    },

    getDirectionSnapshot(): string {
      return JSON.stringify([...this.modelDirection]);
    },

    getServiceSnapshot(): string {
      return JSON.stringify(this.localDoctor?.service || {});
    },

    openDirectionModal(): void {
      this.directionModalSnapshot = this.getDirectionSnapshot();
      this.serviceModalSnapshot = this.getServiceSnapshot();
      this.modalDirection = true;
    },

    setDirectionModalValue(value: boolean): void {
      if (value) {
        this.openDirectionModal();
        return;
      }

      this.closeDirectionModal();
    },

    closeDirectionModal(): void {
      if (!this.localDoctor) {
        this.modalDirection = false;
        return;
      }

      let nextDirections: string[] = [];

      try {
        nextDirections = JSON.parse(this.directionModalSnapshot || "[]");
      } catch {
        nextDirections = [];
      }

      try {
        this.localDoctor.service = JSON.parse(
          this.serviceModalSnapshot || "{}"
        );
      } catch {
        this.localDoctor.service = {};
      }

      this.$doctorStore.setModelDirection(nextDirections);
      this.modalDirection = false;
    },

    applyDirectionModal(): void {
      this.directionModalSnapshot = this.getDirectionSnapshot();
      this.modalDirection = false;
    },

    isDirectionSelected(direction: string): boolean {
      return this.modelDirection.includes(direction);
    },

    setDirectionValue(direction: string, value: boolean): void {
      if (!this.localDoctor) {
        return;
      }

      if (!this.localDoctor.service) {
        this.localDoctor.service = {};
      }

      const nextDirections = [...this.modelDirection];
      const index = nextDirections.indexOf(direction);

      if (value) {
        if (this.localDoctor.service[direction] === undefined) {
          this.localDoctor.service[direction] = JSON.parse(
            JSON.stringify(this.servicesList[direction] || [])
          );
        }

        if (index === -1) {
          nextDirections.push(direction);
        }

        this.$doctorStore.setModelDirection(nextDirections);
        return;
      }

      if (this.localDoctor.service[direction] !== undefined) {
        delete this.localDoctor.service[direction];
      }

      if (index !== -1) {
        nextDirections.splice(index, 1);
      }

      this.$doctorStore.setModelDirection(nextDirections);
    },

    isSpecializationSelected(specialization: string): boolean {
      return !!this.localDoctor?.specialization?.includes(specialization);
    },

    setSpecializationValue(specialization: string, value: boolean): void {
      if (!this.localDoctor) {
        return;
      }

      if (!this.localDoctor.specialization) {
        this.localDoctor.specialization = [];
      }

      const index = this.localDoctor.specialization.indexOf(specialization);

      if (value && index === -1) {
        this.localDoctor.specialization.push(specialization);
        return;
      }

      if (!value && index !== -1) {
        this.localDoctor.specialization.splice(index, 1);
      }
    },

    normalizeDoctorForCompare(sourceDoctor?: doctor | null): doctor | null {
      const doctorToUse = sourceDoctor || this.localDoctor;

      if (!doctorToUse || !doctorToUse._id) {
        return null;
      }

      const preparedDoctor = copyObj(doctorToUse);

      preparedDoctor.photo = this.normalizeDoctorPhoto(
        (doctorToUse as unknown as {photo?: LegacyDoctorPhoto}).photo
      );

      if (this.bufferStatus[preparedDoctor._id] !== undefined) {
        preparedDoctor.status = this.bufferStatus[preparedDoctor._id];
      }

      if (!preparedDoctor.specialization) {
        preparedDoctor.specialization = [];
      }

      if (!preparedDoctor.address) {
        preparedDoctor.address = {};
      }

      if (!preparedDoctor.service) {
        preparedDoctor.service = {};
      }

      if (preparedDoctor.discription == null) {
        preparedDoctor.discription = "";
      }

      if (preparedDoctor.experience == null) {
        preparedDoctor.experience = "";
      }

      if (preparedDoctor.name == null) {
        preparedDoctor.name = "";
      }

      const reorderedService: typeof preparedDoctor.service = {};

      this.modelDirection.forEach((direction) => {
        if (preparedDoctor.service?.[direction] !== undefined) {
          reorderedService[direction] = preparedDoctor.service[direction];
        }
      });

      preparedDoctor.service = reorderedService;

      return preparedDoctor;
    },

    getDoctorSnapshot(sourceDoctor?: doctor | null): string {
      const preparedDoctor = this.normalizeDoctorForCompare(sourceDoctor);
      return preparedDoctor ? JSON.stringify(preparedDoctor) : "";
    },

    updateSavedDoctorSnapshot(sourceDoctor?: doctor | null): void {
      this.savedDoctorSnapshot = this.getDoctorSnapshot(sourceDoctor);
    },

    async saveDoctorChanges(): Promise<void> {
      if (this.isSavingDoctor || !this.localDoctor || !this.localDoctor._id) {
        return;
      }

      const doctorToSave = this.normalizeDoctorForCompare();

      if (!doctorToSave) {
        return;
      }

      this.isSavingDoctor = true;

      try {
        this.updateDoctorInLocalData(doctorToSave);
        this.$doctorStore.updateDoctor(doctorToSave);
        await this.$doctorStore.updateData();
        this.updateSavedDoctorSnapshot(doctorToSave);
      } finally {
        this.isSavingDoctor = false;
      }
    },

    async handleDoctorStatusChange({
      doctor,
      value
    }: {
      doctor: doctor;
      value: boolean;
    }): Promise<void> {
      if (!doctor?._id) {
        return;
      }

      this.applyDoctorStatus(doctor, value);

      const doctorToSave = this.normalizeDoctorForCompare(doctor);

      if (doctorToSave) {
        this.updateDoctorInLocalData(doctorToSave);
        this.$doctorStore.updateDoctor(doctorToSave);
        await this.$doctorStore.updateData();
      }
    },

    updateDoctorInLocalData(updatedDoctor: doctor): void {
      this.data.forEach((group) => {
        group.forEach((doctorItem) => {
          if (doctorItem && doctorItem._id === updatedDoctor._id) {
            Object.assign(doctorItem, updatedDoctor);
          }
        });
      });
    },

    setServicesList(data: setting): void {
      const services: Record<
        string,
        {name: string; cost: string; checked: boolean}[]
      > = {};

      if (!data?.servicesList) {
        return;
      }

      Object.entries(data.servicesList).forEach(([key, directionObj]) => {
        services[key] = directionObj.map((preparedServiceObj) => {
          const servicesObj = this.getObjService(preparedServiceObj);

          return {
            name: Object.values(servicesObj).join(", "),
            cost: servicesObj.cost,
            checked: true
          };
        });
      });

      this.$doctorStore.setServicesList(services);
    },

    openDoctorPanel(doctor: doctor): void {
      this.selectedDoctorId = doctor._id || "";
      this.avatarVersion = 0;

      const doctorCopy = copyObj(doctor);
      const clinicAddresses: string[] = this.getClinic?.addressList || [];
      const currentAddresses = doctorCopy.address
        ? Object.keys(doctorCopy.address)
        : [];

      doctorCopy.photo = this.normalizeDoctorPhoto(
        (doctor as unknown as {photo?: LegacyDoctorPhoto}).photo
      );

      if (!doctorCopy.address) {
        doctorCopy.address = {};
      }

      clinicAddresses.forEach((addressItem) => {
        if (
          doctorCopy.address &&
          doctorCopy.address[addressItem] === undefined
        ) {
          doctorCopy.address[addressItem] = false;
        }
      });

      if (
        doctorCopy.address &&
        !Object.values(doctorCopy.address).includes(true)
      ) {
        const firstKey = Object.keys(doctorCopy.address)[0];

        if (firstKey) {
          doctorCopy.address[firstKey] = true;
        }
      }

      currentAddresses.forEach((addressItem) => {
        if (!clinicAddresses.includes(addressItem)) {
          delete doctorCopy.address?.[addressItem];
        }
      });

      if (!doctorCopy.service) {
        doctorCopy.service = {};
      }

      if (!doctorCopy.specialization) {
        doctorCopy.specialization = [];
      }

      if (doctorCopy.discription == null) {
        doctorCopy.discription = "";
      }

      this.localDoctor = doctorCopy;

      this.$doctorStore.setModelDirection(
        Object.keys(doctorCopy.service || {})
      );

      this.updateSavedDoctorSnapshot(doctorCopy);
    },

    closeDoctorPanel(): void {
      if (this.statusSave) {
        this.unsavedChangesModal = true;
        return;
      }

      this.resetDoctorPanel();
    },

    resetDoctorPanel(): void {
      this.localDoctor = null;
      this.savedDoctorSnapshot = "";
      this.specializationModalSnapshot = "";
      this.directionModalSnapshot = "";
      this.serviceModalSnapshot = "";
      this.unsavedChangesModal = false;
      this.$doctorStore.setModelDirection([]);
      this.selectedDoctorId = "";
      this.closeAllPopups();
    },

    closeDoctorPanelWithoutSave(): void {
      this.resetDoctorPanel();
    },

    async saveDoctorAndClosePanel(): Promise<void> {
      await this.saveDoctorChanges();

      if (!this.isSavingDoctor) {
        this.resetDoctorPanel();
      }
    },

    getStatusDoctor(doc: doctor): boolean {
      if (!doc?._id) {
        return false;
      }

      if (this.bufferStatus[doc._id] !== undefined) {
        return this.bufferStatus[doc._id];
      }

      return !!doc.status;
    },

    applyDoctorStatus(doc: doctor, value: boolean): void {
      if (!doc?._id) {
        return;
      }

      this.bufferStatus[doc._id] = value;
      doc.status = value;

      if (this.localDoctor && this.localDoctor._id === doc._id) {
        this.localDoctor.status = value;
      }

      this.data.forEach((group) => {
        group.forEach((doctorItem) => {
          if (doctorItem && doctorItem._id === doc._id) {
            doctorItem.status = value;
          }
        });
      });
    },

    updateStatusDoctor(doc: doctor, value: boolean): void {
      this.applyDoctorStatus(doc, value);
    },

    setSpecializationMenuValue(index: number, value: boolean): void {
      this.openedDirectionIndex = null;
      this.openedSpecializationIndex = value ? index : null;
    },

    setDirectionMenuValue(index: number, value: boolean): void {
      this.openedSpecializationIndex = null;
      this.openedDirectionIndex = value ? index : null;
    },

    removeSpecialization(index: number): void {
      if (!this.localDoctor?.specialization) {
        return;
      }

      this.localDoctor.specialization.splice(index, 1);
      this.closeSpecializationPopup();
    },

    deleteDirection(index: number): void {
      if (!this.localDoctor) {
        return;
      }

      const nextDirections = [...this.modelDirection];
      const direction = nextDirections[index];

      nextDirections.splice(index, 1);
      this.$doctorStore.setModelDirection(nextDirections);

      if (direction && this.localDoctor.service?.[direction]) {
        delete this.localDoctor.service[direction];
      }

      this.closeDirectionPopup();
    },

    moveDirection(index: number, step: -1 | 1): void {
      if (!this.localDoctor) {
        return;
      }

      const nextDirections = [...this.modelDirection];
      const targetIndex = index + step;

      if (
        index < 0 ||
        targetIndex < 0 ||
        targetIndex >= nextDirections.length
      ) {
        return;
      }

      const currentDirection = nextDirections[index];
      const targetDirection = nextDirections[targetIndex];

      if (!currentDirection || !targetDirection) {
        return;
      }

      nextDirections.splice(index, 1);
      nextDirections.splice(targetIndex, 0, currentDirection);

      this.$doctorStore.setModelDirection(nextDirections);

      if (this.localDoctor.service) {
        const reorderedService: typeof this.localDoctor.service = {};

        nextDirections.forEach((direction) => {
          if (this.localDoctor?.service?.[direction] !== undefined) {
            reorderedService[direction] = this.localDoctor.service[direction];
          }
        });

        this.localDoctor.service = reorderedService;
      }

      this.openedDirectionIndex = targetIndex;
    },

    moveDirectionUp(index: number): void {
      this.moveDirection(index, -1);
    },

    moveDirectionDown(index: number): void {
      this.moveDirection(index, 1);
    },

    moveSpecialization(index: number, step: -1 | 1): void {
      if (!this.localDoctor?.specialization) {
        return;
      }

      const targetIndex = index + step;

      if (
        index < 0 ||
        targetIndex < 0 ||
        targetIndex >= this.localDoctor.specialization.length
      ) {
        return;
      }

      const list = this.localDoctor.specialization;

      [list[index], list[targetIndex]] = [list[targetIndex], list[index]];

      this.openedSpecializationIndex = targetIndex;
    },

    moveSpecializationUp(index: number): void {
      this.moveSpecialization(index, -1);
    },

    moveSpecializationDown(index: number): void {
      this.moveSpecialization(index, 1);
    },

    closeSpecializationPopup(): void {
      this.openedSpecializationIndex = null;
    },

    closeDirectionPopup(): void {
      this.openedDirectionIndex = null;
    },

    closeAllPopups(): void {
      this.closeSpecializationPopup();
      this.closeDirectionPopup();
    },

    openUploadDialog(): void {
      const input = this.$refs.photoInput as HTMLInputElement | undefined;
      input?.click();
    },

    async setImgDoctor(e: Event): Promise<void> {
      const id = this.localDoctor?._id;

      if (!id) {
        return;
      }

      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        target.value = "";
        return;
      }

      const extension = file.name.split(".").pop() || "jpg";
      const random = String(Math.floor(Math.random() * 1000000));
      const newName = `${id}_${random}.${extension}`;
      const newFile = new File([file], newName, {type: file.type});

      const formData = new FormData();

      formData.append("file", newFile);

      try {
        const data = await API.updateDoctor({body: formData});

        if (!this.localDoctor) {
          return;
        }

        let serverPhoto = "";

        if (Array.isArray(data)) {
          const updatedDoctor = data.find((doctorItem: doctor) => {
            return doctorItem?._id === id;
          });

          serverPhoto = this.normalizeDoctorPhoto(
            (updatedDoctor as unknown as {photo?: LegacyDoctorPhoto})?.photo
          );
        }

        this.localDoctor.photo = serverPhoto || newName;
        this.avatarVersion += 1;
      } catch (error) {
        console.error("Ошибка при загрузке фото врача:", error);
      } finally {
        target.value = "";
      }
    },

    clearDoctorPhoto(): void {
      if (!this.localDoctor || !this.localDoctor._id) {
        return;
      }

      this.localDoctor.photo = "";
      this.avatarVersion += 1;
    },

    getImgDoctor(doctorData: doctor | null): string {
      const photoName = this.normalizeDoctorPhoto(
        (doctorData as unknown as {photo?: LegacyDoctorPhoto})?.photo
      );

      if (!photoName) {
        return "";
      }

      return `url('https://${this.DOMAIN}/api/img?size=250x250&name=${encodeURIComponent(photoName)}&v=${this.avatarVersion}')`;
    },

    async deleteDoctor(): Promise<void> {
      if (!this.localDoctor || !this.localDoctor._id) {
        return;
      }

      const id = this.localDoctor._id;

      this.localDoctor.deleteDoctor = true;

      const doctorToSave = this.normalizeDoctorForCompare();

      if (!doctorToSave) {
        return;
      }

      this.updateDoctorInLocalData(doctorToSave);
      this.$doctorStore.updateDoctor(doctorToSave);
      await this.$doctorStore.updateData();

      this.deleteDoctorModal = false;
      this.resetDoctorPanel();

      await nextTick();

      const offsetTop = document.getElementById(id)?.offsetTop ?? 0;
      window.scrollTo(0, offsetTop + 120);
    },

    getDirectionActions(index: number): ListActionItem[] {
      return [
        {
          id: `direction-${index}-move-up`,
          text: "Переместить вверх",
          icon: this.chevronUpIcon,
          variant: "button",
          disabled: !(index > 0),
          handler: () => {
            this.moveDirectionUp(index);
            this.closeDirectionPopup();
          }
        },
        {
          id: `direction-${index}-move-down`,
          text: "Переместить вниз",
          icon: this.chevronDownIcon,
          variant: "button",
          disabled: !(index < this.modelDirection.length - 1),
          handler: () => {
            this.moveDirectionDown(index);
            this.closeDirectionPopup();
          }
        },
        {
          id: `direction-${index}-delete`,
          text: "Удалить",
          icon: this.deleteIconError,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => {
            this.deleteDirection(index);
          }
        }
      ];
    },

    getSpecializationActions(index: number): ListActionItem[] {
      if (!this.localDoctor) {
        return [];
      }

      return [
        {
          id: `specialization-${index}-move-up`,
          text: "Переместить вверх",
          icon: this.chevronUpIcon,
          variant: "button",
          disabled: !(index > 0),
          handler: () => {
            this.moveSpecializationUp(index);
            this.closeSpecializationPopup();
          }
        },
        {
          id: `specialization-${index}-move-down`,
          text: "Переместить вниз",
          icon: this.chevronDownIcon,
          variant: "button",
          disabled: !(index < this.localDoctor.specialization.length - 1),
          handler: () => {
            this.moveSpecializationDown(index);
            this.closeSpecializationPopup();
          }
        },
        {
          id: `specialization-${index}-delete`,
          text: "Удалить",
          icon: this.deleteIconError,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => {
            this.removeSpecialization(index);
          }
        }
      ];
    }
  },

  computed: {
    statusDataLouding() {
      return this.$doctorStore.getStatusDataLouding;
    },

    data(): doctor[][] {
      return this.$doctorStore.getData;
    },

    getClinic(): clinic {
      return this.$clinicStore.getClinic;
    },

    getSetting() {
      return this.$clinicStore.getSetting;
    },

    bufferStatus() {
      return this.$doctorStore.getBuferStatus;
    },

    servicesList() {
      return this.$doctorStore.getServicesList;
    },

    modelDirection(): string[] {
      return this.$doctorStore.getModelDirection;
    },

    visibleData(): doctor[] {
      const allDoctors = this.data.flat().filter((doctorItem) => {
        return !!doctorItem && !doctorItem.deleteDoctor;
      });

      return allDoctors;
    },

    statusSave(): boolean {
      if (!this.localDoctor || !this.localDoctor._id) {
        return false;
      }

      return this.getDoctorSnapshot() !== this.savedDoctorSnapshot;
    },

    statusSaveSpecializationModal(): boolean {
      if (!this.modalSpecialization) {
        return false;
      }

      return (
        this.getSpecializationSnapshot() !== this.specializationModalSnapshot
      );
    },

    statusSaveDirectionModal(): boolean {
      if (!this.modalDirection) {
        return false;
      }

      return this.getDirectionSnapshot() !== this.directionModalSnapshot;
    },

    actionsUploadDoctorPhoto(): ListActionItem[] {
      return [
        {
          text: "Загрузить",
          icon: uploadIcon,
          handler: this.openUploadDialog,
          variant: "button"
        },
        {
          text: "Удалить",
          icon: deleteIconError,
          handler: this.clearDoctorPhoto,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError)
        }
      ];
    },

    buttonDeleteDoctorModal(): ListActionItem[] {
      return [
        {
          text: "Удалить",
          handler: this.deleteDoctor
        },
        {
          text: "Отмена",
          handler: this.closeDeleteDoctorModal
        }
      ];
    },
    saveModalButtons(): ListActionItem[] {
      return [
        {
          text: "Не сохранять",
          handler: this.closeDoctorPanelWithoutSave
        },
        {
          text: "Сохранить",
          disabled: this.isSavingDoctor,
          handler: this.saveDoctorAndClosePanel
        }
      ];
    }
  },

  watch: {
    getSetting(data): void {
      this.setServicesList(data);
    }
  }
});
</script>
